import { useMemo } from 'react'
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import {
  getDurationOptionsDefault,
} from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

function formatMonthLabel(year: string | undefined, month: string | undefined, intl: IntlShape): string {
  if (!year || !month) {
    return [month, year].filter(Boolean).join(' ')
  }

  return intlDateFormatter(
    `${year}-${month.padStart(2, '0')}-01`,
    INTL_SHORT_MONTH,
    intl.locale,
  )
}

// Input string is in the format of: `03-2022,04-2022,1-2023`
export function transformMonthString(
  flexibleMonths: string,
  intl: IntlShape = getCurrentIntl(),
): string {
  if (!flexibleMonths) {
    return ''
  }

  const monthsWithYears = flexibleMonths.split(',').sort()
  const length = monthsWithYears.length

  const [year1, month1] = monthsWithYears[0]?.split('-') ?? []
  const firstMonthLabel = formatMonthLabel(year1, month1, intl)

  if (length === 1) {
    return intl.formatMessage(
      {
        id: 'KKj_16SoGitsdrBbplWp',
        defaultMessage: '{monthLabel} {year}',
        description: 'Flexible date month summary showing a single selected month with its year',
      },
      {
        monthLabel: firstMonthLabel,
        year: year1,
      },
    )
  }

  const [year2, month2] = monthsWithYears[1]?.split('-') ?? []
  const secondMonthLabel = formatMonthLabel(year2, month2, intl)

  const monthSummary = year1 === year2 ?
      intl.formatMessage(
        {
          id: '_hyAxwKBDuN3BQ-ivH6F',
          defaultMessage: '{firstMonthLabel}, {secondMonthLabel} {year}',
          description: 'Flexible date month summary showing two selected months that share the same year',
        },
        {
          firstMonthLabel,
          secondMonthLabel,
          year: year1,
        },
      ) :
      intl.formatMessage(
        {
          id: 'iGha2Iq0GcpAKPtUn8mp',
          defaultMessage: '{firstMonthLabel} {firstYear}, {secondMonthLabel} {secondYear}',
          description: 'Flexible date month summary showing two selected months that are in different years',
        },
        {
          firstMonthLabel,
          firstYear: year1,
          secondMonthLabel,
          secondYear: year2,
        },
      )

  if (length === 2) {
    return monthSummary
  }

  // Match the compact search chip behavior by showing the first two visible
  // months, then how many more months are selected.
  return intl.formatMessage(
    {
      id: 'dJ4g6jQWtIS64gdHhZja',
      defaultMessage: '{monthSummary} +{remainingMonthCount}',
      description: 'Flexible date month summary showing the first two visible months and how many extra months are selected',
    },
    {
      monthSummary,
      remainingMonthCount: length - 2,
    },
  )
}

export function prettyFlexibleDates(flexibleMonths: string = '', flexibleNights?: string, durationOptions?: Array<DurationOption>, intl?: IntlShape): string {
  const resolvedIntl = intl ?? getCurrentIntl()
  const resolvedDurationOptions = durationOptions ?? getDurationOptionsDefault(resolvedIntl)
  const duration = resolvedDurationOptions.find((option) => {
    return option.id === flexibleNights
  })
  const monthString = transformMonthString(flexibleMonths, resolvedIntl)
  let text = duration !== undefined ? duration.caption : resolvedIntl.formatMessage({
    id: 'dE9pbsBTAOa95H8UX-4x',
    defaultMessage: 'I’m flexible',
    description: 'Placeholder text for flexible date search when no duration is selected',
  })
  if (flexibleMonths?.length > 0) {
    text = resolvedIntl.formatMessage(
      {
        id: '1P1lOTuJdexWD8EWyoC4',
        defaultMessage: '{durationLabel} · {monthString}',
        description: 'Flexible date label with duration and selected months',
      },
      {
        durationLabel: text,
        monthString,
      },
    )
  }
  return text
}

interface Props {
  onDateToggle: (e: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  flexibleMonths?: string
  flexibleNightsId?: string
  durationOptionsCustom?: Array<DurationOption>
  showAIGlow?: boolean
}

function FlexibleDateSearchField({
  className,
  onDateToggle,
  flexibleMonths,
  flexibleNightsId,
  durationOptionsCustom,
  showAIGlow,
}: Props) {
  const intl = useIntl()
  const text = useMemo(() => prettyFlexibleDates(flexibleMonths, flexibleNightsId, durationOptionsCustom, intl), [durationOptionsCustom, flexibleMonths, flexibleNightsId, intl])
  return (
    <SearchFormField
      className={className}
      label={intl.formatMessage({
        id: 'wG2gmKGMOFCuLssrqrqz',
        defaultMessage: 'When?',
        description: 'Label for flexible date search field in the search and discovery interface',
      })}
      placeholder={text}
      onClick={onDateToggle}
      data-testid="search-flexible-search-trigger-input"
      showAIGlow={showAIGlow}
    />
  )
}

export default FlexibleDateSearchField
